import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { coverSrc } from "../lib/images";
import html2canvas from "html2canvas";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurator } from "../lib/useCurator";
import { MaskedLines, FadeUp } from "../components/Reveal";

const REF_LABEL = { book: "Book", film: "Film", tv: "Series", youtube: "Video", essay: "Essay" };

export default function CurriculumDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [cur, setCur] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [loadError, setLoadError] = useState(null); // "archived" | "missing"
  const curator = useCurator(id);
  const certRef = useRef(null);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Share link copied");
  };
  const downloadCert = async () => {
    if (!certRef.current) return;
    try {
      const surface = getComputedStyle(document.documentElement).getPropertyValue("--c-surface").trim();
      const bg = surface ? `rgb(${surface})` : "#141311";
      const canvas = await html2canvas(certRef.current, { backgroundColor: bg, scale: 2 });
      const link = document.createElement("a");
      link.download = `${(cur?.title || "pathway").replace(/\s+/g, "-").toLowerCase()}-certificate.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Keepsake downloaded");
    } catch { toast.error("Could not generate image"); }
  };

  useEffect(() => {
    setLoadError(null);
    api
      .get(`/curriculums/${id}`)
      .then((r) => setCur(r.data))
      .catch((e) => setLoadError(e?.response?.status === 410 ? "archived" : "missing"));
  }, [id]);

  useEffect(() => {
    if (user) {
      api.get(`/progress/${id}`).then((r) => setCompleted(r.data.completed || [])).catch(() => {});
    } else {
      setCompleted([]);
    }
  }, [id, user]);

  const flatIds = useMemo(() => {
    const res = [];
    let k = 0;
    (cur?.modules || []).forEach((mod) => {
      const row = [];
      (mod.steps || []).forEach(() => { row.push(k++); });
      res.push(row);
    });
    return res;
  }, [cur]);
  const totalSteps = flatIds.flat().length;

  const [pulseIdx, setPulseIdx] = useState(null);

  const toggle = async (index) => {
    if (!user) { login(); return; }
    try {
      const r = await api.post(`/progress/${id}/toggle`, { item_index: index });
      setCompleted(r.data.completed);
      const nowDone = r.data.completed.includes(index);
      if (nowDone) setPulseIdx(index);
      toast(nowDone ? "Marked complete" : "Marked incomplete");
    } catch {
      toast.error("Could not save progress");
    }
  };

  if (loadError) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid={loadError === "archived" ? "curriculum-archived" : "curriculum-not-found"}>
      <p className="overline text-crimson mb-5">{loadError === "archived" ? "Archived" : "Not found"}</p>
      {loadError === "archived" ? (
        <>
          <h1 className="font-serif text-4xl lg:text-6xl font-light tracking-tight text-vellum leading-[1.05] max-w-3xl">
            Oh no! This page has been archived.
          </h1>
          <p className="mt-6 text-lg text-vellum-muted">
            Email{" "}
            <a href="mailto:support@lab4t4.com" data-testid="curriculum-archived-email" className="text-brass hover:text-crimson underline decoration-brass/40 underline-offset-4 transition-colors">
              support@lab4t4.com
            </a>{" "}
            for any enquiries
          </p>
        </>
      ) : (
        <h1 className="font-serif text-4xl lg:text-6xl font-light tracking-tight text-vellum">This pathway isn&apos;t here.</h1>
      )}
      <Link to="/curriculums" data-testid="curriculum-archived-back" className="mt-10 font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 border border-crimson text-crimson hover:bg-crimson hover:text-onaccent transition-colors">
        All Curriculums
      </Link>
    </div>
  );

  if (!cur) return <div className="min-h-screen flex items-center justify-center"><p className="font-serif text-3xl text-vellum-muted animate-pulse">Loading…</p></div>;

  const pct = totalSteps ? Math.round((completed.length / totalSteps) * 100) : 0;

  return (
    <div data-testid="curriculum-detail" className="pt-16 lg:pt-20">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={coverSrc(cur.image_url, "hero")}
            alt=""
            fetchPriority="high"
            decoding="async"
            onLoad={() => setHeroLoaded(true)}
            style={{
              objectPosition: `${cur.image_focal?.x ?? 50}% ${cur.image_focal?.y ?? 50}%`,
              ...(cur.image_focal?.flip ? { transform: "scaleX(-1)" } : {}),
              opacity: heroLoaded ? 1 : 0,
            }}
            className="w-full h-full object-cover brightness-[0.35] contrast-105 gpu-layer transition-opacity duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/50" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <FadeUp>
            <span className="flex flex-wrap items-center justify-between gap-4">
              <Link to="/curriculums" className="overline text-brass hover:text-onaccent transition-colors">← All Curriculums</Link>
              {curator && (
                <Link
                  to={`/admin?tab=curriculums&edit=${cur.id}`}
                  data-testid="curriculum-curator-edit"
                  title="Open this curriculum in the editor"
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border border-brass/50 text-brass bg-ink/70 hover:bg-brass hover:text-ink transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit this curriculum
                </Link>
              )}
            </span>
          </FadeUp>
          <p className="overline mt-8 mb-6 text-crimson">{cur.field} · {cur.modules?.length || 0} modules · {totalSteps} steps</p>
          <h1 className="font-serif font-light tracking-tighter leading-[0.88] text-vellum text-[clamp(2.5rem,8vw,7rem)] max-w-5xl">
            <MaskedLines lines={[cur.title]} />
          </h1>
          <FadeUp delay={0.2}><p className="mt-8 max-w-2xl text-vellum-muted text-lg leading-relaxed">{cur.description}</p></FadeUp>
          {cur.overview && (
            <FadeUp delay={0.28}>
              <div className="mt-8 flex flex-wrap gap-x-3 gap-y-2 items-center" data-testid="curriculum-overview">
                {cur.overview.split("→").map((s, i, arr) => (
                  <span key={`${s}-${i}`} className="flex items-center gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-brass">{s.trim()}</span>
                    {i < arr.length - 1 && <span className="text-crimson">→</span>}
                  </span>
                ))}
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      {/* Progress bar */}
      <div className="sticky top-16 lg:top-20 z-40 bg-ink/85 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-4 flex items-center gap-6">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted whitespace-nowrap">Progress {completed.length}/{totalSteps}</span>
          <div className="flex-1 h-px bg-line relative">
            <motion.div className="absolute left-0 top-0 h-px bg-crimson" animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
          </div>
          <span className="font-mono text-xs text-brass">{pct}%</span>
          {!user && (
            <button onClick={login} data-testid="progress-signin" className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted hover:text-crimson transition-colors">
              <Lock className="w-3.5 h-3.5" /> Sign in to track
            </button>
          )}
        </div>
      </div>

      {/* Modules */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {!user && (
          <FadeUp>
            <div className="mb-16 border border-line bg-ink-800 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-vellum-muted max-w-lg">Sign in to track your progress, create a TBR list, and add notes.</p>
              <button onClick={login} data-testid="curriculum-signin-btn" className="font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 border border-crimson text-crimson hover:bg-crimson hover:text-onaccent transition-colors whitespace-nowrap">Sign in</button>
            </div>
          </FadeUp>
        )}

        <div className="space-y-20">
          {cur.modules.map((mod, mi) => (
            <FadeUp key={`${mod.title}-${mi}`}>
              <div data-testid={`module-${mi}`}>
                <div className="border-b border-line pb-6 mb-8">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-crimson">Module {mi + 1}</span>
                  <h2 className="mt-2 font-serif text-3xl lg:text-5xl font-light text-vellum tracking-tight">{mod.title}</h2>
                  {mod.note && <p className="mt-3 max-w-2xl text-vellum-muted leading-relaxed">{mod.note}</p>}
                </div>

                <div className="divide-y divide-line">
                  {mod.steps.map((step, si) => {
                    const idx = flatIds[mi][si];
                    const done = completed.includes(idx);
                    return (
                      <div
                        key={`${step.core?.title || "step"}-${si}`}
                        data-testid={`step-${mi}-${si}`}
                        onAnimationEnd={() => { if (pulseIdx === idx) setPulseIdx(null); }}
                        className={`grid grid-cols-[auto_1fr] lg:grid-cols-[auto_1fr_auto] gap-x-6 gap-y-6 lg:gap-x-10 py-8 lg:py-10 px-4 -mx-4 items-start transition-opacity ${done ? "opacity-60" : ""} ${pulseIdx === idx ? "step-pulse" : ""}`}
                      >
                        <span className="mt-2 font-mono text-sm text-vellum-faint shrink-0 w-11 text-center" aria-hidden="true">{String(si + 1).padStart(2, "0")}</span>
                        <div>
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">Core · {REF_LABEL[step.core.ref_type] || step.core.ref_type}</span>
                          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <h3 className={`font-serif text-3xl lg:text-4xl font-light text-vellum ${done ? "line-through decoration-crimson/60" : ""}`}>{step.core.title}</h3>
                            {step.core.creator && <span className="font-mono text-xs text-vellum-faint">{step.core.creator}</span>}
                          </div>
                          {step.core.note && <p className="mt-3 max-w-2xl text-vellum-muted italic leading-relaxed">{step.core.note}</p>}
                          {step.core.link && (
                            <a href={step.core.link} target="_blank" rel="noreferrer" data-testid={`step-link-${idx}`} className="mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted hover:text-crimson transition-colors">
                              Open the core text <ArrowUpRight className="w-4 h-4" />
                            </a>
                          )}
                          {step.companion?.title && (
                            <div className="mt-6 pl-5 border-l border-line" data-testid={`companion-${idx}`}>
                              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass">Companion · use alongside</span>
                              <div className="flex flex-wrap items-baseline gap-x-3 mt-1">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass/70">{REF_LABEL[step.companion.ref_type] || step.companion.ref_type}</span>
                                <span className="font-serif text-xl text-vellum">{step.companion.title}</span>
                                {step.companion.creator && <span className="font-mono text-xs text-vellum-faint">{step.companion.creator}</span>}
                              </div>
                              {step.companion.note && <p className="text-sm text-vellum-muted italic leading-relaxed mt-1">{step.companion.note}</p>}
                              {step.companion.link && <a href={step.companion.link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted hover:text-brass transition-colors">Open companion <ArrowUpRight className="w-3.5 h-3.5" /></a>}
                            </div>
                          )}
                          {step.optional?.title && (
                            <div className="mt-4 pl-5 border-l border-line" data-testid={`optional-${idx}`}>
                              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">Optional · excellent but not necessary</span>
                              <div className="flex flex-wrap items-baseline gap-x-3 mt-1">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">{REF_LABEL[step.optional.ref_type] || step.optional.ref_type}</span>
                                <span className="font-serif text-xl text-vellum/90">{step.optional.title}</span>
                                {step.optional.creator && <span className="font-mono text-xs text-vellum-faint">{step.optional.creator}</span>}
                              </div>
                              {step.optional.note && <p className="text-sm text-vellum-muted italic leading-relaxed mt-1">{step.optional.note}</p>}
                              {step.optional.link && <a href={step.optional.link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted hover:text-onaccent transition-colors">Open <ArrowUpRight className="w-3.5 h-3.5" /></a>}
                            </div>
                          )}
                        </div>

                        {/* Completion control — after the content, where the eye lands last */}
                        <div className="col-start-2 lg:col-start-3 lg:pl-4 lg:sticky lg:top-28">
                          <label
                            className={`group/check cursor-pointer inline-flex items-center gap-3 border px-4 py-3 min-h-[44px] transition-colors whitespace-nowrap ${
                              done
                                ? "border-crimson/70 text-crimson hover:border-crimson"
                                : "border-line text-vellum-muted hover:border-vellum hover:text-vellum"
                            }`}
                            data-testid={`toggle-label-${idx}`}
                          >
                            <input
                              type="checkbox"
                              checked={done}
                              onChange={() => toggle(idx)}
                              data-testid={`toggle-${idx}`}
                              aria-label={`${done ? "Mark as not complete" : "Mark as complete"}: ${step.core.title}`}
                              className="sr-only peer"
                            />
                            <span
                              aria-hidden="true"
                              className={`w-6 h-6 shrink-0 border flex items-center justify-center transition-colors duration-300 peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brass ${
                                done
                                  ? "bg-crimson border-crimson text-onaccent"
                                  : "border-line text-transparent group-hover/check:border-vellum group-hover/check:text-vellum-faint"
                              }`}
                            >
                              <Check className="w-4 h-4" />
                            </span>
                            <span className="font-mono text-[11px] uppercase tracking-[0.18em]">
                              {done ? "Completed" : "Mark as complete"}
                            </span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        {totalSteps > 0 && pct === 100 && (
          <FadeUp>
            <div className="mt-16" data-testid="certificate">
              <div ref={certRef} className="relative border border-brass/50 bg-ink-800 px-8 lg:px-16 py-14 text-center overflow-hidden">
                <div className="absolute inset-3 border border-line pointer-events-none" />
                <div className="relative">
                  <p className="overline text-brass mb-8">Certificate of Completion</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-vellum-faint mb-3">This certifies that</p>
                  <p className="font-serif text-4xl lg:text-5xl text-vellum">{user?.name || "A dedicated reader"}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-vellum-faint my-5">has completed the pathway</p>
                  <p className="font-serif text-3xl lg:text-4xl italic text-brass leading-tight">{cur.title}</p>
                  <p className="mt-3 text-vellum-muted">{cur.field} · {totalSteps} steps</p>
                  <div className="mt-10 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full border border-crimson flex items-center justify-center text-crimson font-mono text-xs tracking-[0.15em]">TS</span>
                  </div>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-vellum-faint">The Private Library of Tirthak Saha · {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <button onClick={downloadCert} data-testid="cert-download" className="font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors">Download keepsake</button>
                <button onClick={copyLink} data-testid="cert-share" className="font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors">Copy share link</button>
              </div>
            </div>
          </FadeUp>
        )}
      </section>
    </div>
  );
}
